import { useState, type FormEvent } from "react";

import { Button, Modal, Select, Toggle } from "@/components/common";
import { extractErrorMessage } from "@/services/axiosConfig";
import type { OperatorType } from "@/types/contract";
import type { Organization } from "@/types/organization";
import { useUpdateOrganization } from "@/hooks/useOrganizations";

const OPERATOR_OPTIONS: { value: OperatorType; label: string }[] = [
  { value: "tributo_devido", label: "Tributo Devido" },
  { value: "organization", label: "Organização parceira" },
  { value: "workspace", label: "Workspace próprio" },
];

/** ARCH-002: modal canônico de EDIÇÃO de organização. A criação tem um único modal
 * canônico (OrgCreateModal, com link/manual). Aqui só editamos uma org existente —
 * o CNPJ é imutável e o payload espelha o update do mesmo endpoint. */
type Props = { mode: "edit"; workspaceId: number; org: Organization; onClose: () => void };

export function OrgFormModal({ workspaceId, org, onClose }: Props) {
  const updateMut = useUpdateOrganization(workspaceId, org.id);

  const cnpj = org.cnpj_masked;
  const [legalName, setLegalName] = useState(org.legal_name);
  const [nickname, setNickname] = useState(org.nickname ?? "");
  const [email, setEmail] = useState("");
  const [thirdParty, setThirdParty] = useState(org.third_party_funds);
  // "" = herdar o operator_type do workspace.
  const [operatorType, setOperatorType] = useState<OperatorType | "">(org.operator_type ?? "");
  const [tdSharePct, setTdSharePct] = useState(org.default_td_share_pct ?? "");
  const [error, setError] = useState<string | null>(null);

  const busy = updateMut.isPending;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await updateMut.mutateAsync({
        legal_name: legalName.trim(),
        nickname: nickname.trim() || undefined,
        email: email.trim() || undefined,
        third_party_funds: thirdParty,
        operator_type: operatorType || null,
        default_td_share_pct: tdSharePct.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      onClose={onClose}
      title="Editar organização"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="org-form" disabled={busy}>
            {busy ? "Salvando…" : "Salvar"}
          </Button>
        </>
      }
    >
      <form id="org-form" onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {error && <div className="form-error">{error}</div>}
        <label className="form-field">
          <span>CNPJ</span>
          <input
            className="form-input"
            value={cnpj}
            placeholder="00.000.000/0000-00"
            disabled
            inputMode="numeric"
            readOnly
          />
        </label>
        <label className="form-field">
          <span>Razão social</span>
          <input className="form-input" value={legalName} onChange={(e) => setLegalName(e.target.value)} required />
        </label>
        <label className="form-field">
          <span>Nome fantasia</span>
          <input className="form-input" value={nickname} onChange={(e) => setNickname(e.target.value)} />
        </label>
        <label className="form-field">
          <span>E-mail de contato</span>
          <input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={org?.email_masked ?? ""} />
        </label>
        <label className="form-field">
          <span>Modelo de operação dos contratos</span>
          <Select<OperatorType>
            value={operatorType || null}
            onChange={(v) => setOperatorType(v || "")}
            options={OPERATOR_OPTIONS}
            placeholder="Herdar do workspace"
            clearable
            clearLabel="Herdar do workspace"
          />
        </label>
        <label className="form-field">
          <span>Participação TD padrão</span>
          <input
            className="form-input"
            value={tdSharePct}
            onChange={(e) => setTdSharePct(e.target.value)}
            placeholder="ex: 0.85 (opcional)"
            inputMode="decimal"
          />
        </label>
        <Toggle
          checked={thirdParty}
          onChange={setThirdParty}
          label="Recursos de terceiros"
          sub="A organização administra recursos de terceiros"
        />
      </form>
    </Modal>
  );
}
